import type { AccountCoverageSummary } from './accountAnalytics';
import type { Client, Project, ProjectUpdate, Task } from '../types/crm';
import { CRM_TIMEZONE, formatSingaporeDate } from '../utils/dateTime';
import { idsMatch } from '../utils/crmHelpers';
import { isSystemProjectUpdate } from '../utils/projectHelpers';
import {
  buildTaskTitlePieSlicesFromTasks,
  categorizeTaskTitle,
  formatTaskMonthLabel,
  type TaskTitlePieSlice,
} from '../utils/taskHelpers';

/** Unique New Visit accounts required per calendar month. */
export const NEW_VISIT_MONTHLY_TARGET = 10;
/** Minimum visits (any task title) per weekday (Mon–Fri). */
export const DAILY_VISIT_TARGET = 8;
/** Accounts with this many visits in the selected week are “high frequency”. */
export const HIGH_FREQ_VISIT_THRESHOLD = 3;
/** Ongoing projects need a non-system update within this many days. */
export const PROJECT_UPDATE_ON_TIME_DAYS = 14;

const DUTY_PATTERN =
  /\b(will (send|call|visit|email|prepare|follow)|follow[- ]?up|please |need to |to (send|call|visit|prepare|confirm)|action( item)?|assigned to|remind|promise[ds]?)\b/i;
const FOLLOW_UP_DONE_PATTERN =
  /\b(follow[- ]?ed up|followed up|as promised|sent the|called back|already (sent|called|emailed)|completed the)\b/i;

export type WeeklyVisitRow = {
  taskId: string;
  title: string;
  status: string;
  dayKey: string;
  dayLabel: string;
  ownerId: string;
  ownerLabel: string;
  clientId: string | null;
  companyName: string;
  code: string;
  description: string;
  hasDescription: boolean;
};

export type WeeklyOwnerStat = {
  ownerId: string;
  ownerLabel: string;
  visitCount: number;
  accountCount: number;
  withDescription: number;
  descriptionRate: number;
  /** Unique New Visit accounts this calendar month for this person only. */
  newVisitUnique: number;
  newVisitTarget: number;
  newVisitMet: boolean;
  newVisitRemaining: number;
  /** Weekdays this week where this person reached the daily visit target. */
  workdaysMet: number;
  workdayCount: number;
  dailyTargetMet: boolean;
};

export type WeeklyDayStat = {
  dayKey: string;
  dayLabel: string;
  weekdayShort: string;
  visitCount: number;
  isWorkday: boolean;
  metDailyTarget: boolean;
};

export type WeeklyProjectStat = {
  total: number;
  ongoing: number;
  onHold: number;
  completed: number;
  problemProjects: Array<{
    id: string;
    title: string;
    companyName: string;
    status: string;
  }>;
};

export type WeeklyNewVisitMonthStat = {
  monthKey: string;
  monthLabel: string;
  /**
   * For a single-person scope: that person's unique New Visit count.
   * For team (All) scope: count of staff who already met the per-person target (not a summed total).
   */
  uniqueCount: number;
  target: number;
  met: boolean;
  remaining: number;
  /** True when the top-level scorecard is a staff-hit rate, not one person's unique accounts. */
  isTeamRate: boolean;
  staffMet: number;
  staffCount: number;
};

export type WeeklyHighFreqAccount = {
  clientId: string;
  companyName: string;
  code: string;
  visitCount: number;
};

export type WeeklyStaleProject = {
  id: string;
  title: string;
  companyName: string;
  lastUpdateAt: string | null;
  daysSinceUpdate: number | null;
};

export type WeeklyProjectUpdateHealth = {
  ongoingTotal: number;
  onTime: number;
  stale: number;
  neverUpdated: number;
  staleProjects: WeeklyStaleProject[];
};

export type WeeklyDutyFollowUp = {
  taskId: string;
  companyName: string;
  dayLabel: string;
  ownerLabel: string;
  snippet: string;
  followedUp: boolean;
};

export type WeeklyDutyFollowUpStat = {
  withDuty: number;
  followedUp: number;
  openCount: number;
  items: WeeklyDutyFollowUp[];
};

export type WeeklyThemeHit = {
  theme: string;
  count: number;
  examples: string[];
};

export type WeeklyNarrative = {
  summary: string;
  recommendation: string;
  findings: string[];
};

export type CrmWeeklySummaryReport = {
  weekStart: string;
  weekEnd: string;
  weekLabel: string;
  visitCount: number;
  accountCount: number;
  withDescription: number;
  withoutDescription: number;
  descriptionRate: number;
  overdueOpenTasks: number;
  projects: WeeklyProjectStat;
  newVisitsMonth: WeeklyNewVisitMonthStat;
  dailyTarget: number;
  /**
   * Single-person scope: weekdays this person met the daily target.
   * Team scope: number of staff who met the daily target on every weekday this week.
   */
  workdaysMet: number;
  workdayCount: number;
  /** Team All view — targets are per staff, not summed. */
  isTeamScope: boolean;
  staffDailyTargetMet: number;
  staffCount: number;
  highFrequency: WeeklyHighFreqAccount[];
  projectUpdateHealth: WeeklyProjectUpdateHealth;
  dutyFollowUps: WeeklyDutyFollowUpStat;
  visits: WeeklyVisitRow[];
  byDay: WeeklyDayStat[];
  byOwner: WeeklyOwnerStat[];
  titleSlices: TaskTitlePieSlice[];
  themes: WeeklyThemeHit[];
  notVisited: AccountCoverageSummary[];
  notVisitedCount: number;
  narrative: WeeklyNarrative;
};

const THEME_RULES: Array<{ theme: string; pattern: RegExp }> = [
  { theme: 'Quotation / pricing', pattern: /\b(quot(e|ation)|pric(e|ing)|discount|cost)\b/i },
  { theme: 'Sample / trial', pattern: /\b(sample|trial|demo|tasting)\b/i },
  { theme: 'Delivery / stock', pattern: /\b(deliver(y|ed)?|stock|replenish|shortage|urgent)\b/i },
  { theme: 'Complaint / issue', pattern: /\b(complain|issue|problem|damag|wrong|return|reject)\b/i },
  { theme: 'Payment / credit', pattern: /\b(pay(ment)?|invoice|credit|outstanding|overdue|collect)\b/i },
  { theme: 'Competitor', pattern: /\b(competitor|rival|switch(ed|ing)?|alternative)\b/i },
  { theme: 'Follow-up / next step', pattern: /\b(follow[- ]?up|will (send|call|visit)|next (week|visit)|to (send|prepare|confirm))\b/i },
  { theme: 'Relationship / courtesy', pattern: /\b(thank|appreciate|relationship|catch up|courtesy)\b/i },
];

function isCancelledTask(task: Task): boolean {
  return String(task.status ?? '')
    .trim()
    .toLowerCase() === 'cancelled';
}

function safeText(value: unknown, fallback = '—'): string {
  if (value == null) return fallback;
  const text = String(value).trim();
  return text || fallback;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function ymdFromUtcDate(d: Date): string {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

function parseYmdUtc(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return new Date(Date.UTC(y, mo - 1, d));
}

/** YYYY-MM-DD in Asia/Singapore for "now". */
export function singaporeTodayYmd(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CRM_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** Monday (inclusive) of the Singapore calendar week containing `ymd`. */
export function mondayOfWeekContaining(ymd: string): string {
  const utc = parseYmdUtc(ymd) ?? parseYmdUtc(singaporeTodayYmd());
  if (!utc) return singaporeTodayYmd();
  const dow = utc.getUTCDay(); // 0 = Sun
  const offset = dow === 0 ? -6 : 1 - dow;
  utc.setUTCDate(utc.getUTCDate() + offset);
  return ymdFromUtcDate(utc);
}

export function sundayOfWeek(mondayYmd: string): string {
  const utc = parseYmdUtc(mondayYmd);
  if (!utc) return mondayYmd;
  utc.setUTCDate(utc.getUTCDate() + 6);
  return ymdFromUtcDate(utc);
}

export function shiftWeekMonday(mondayYmd: string, deltaWeeks: number): string {
  const utc = parseYmdUtc(mondayOfWeekContaining(mondayYmd));
  if (!utc) return mondayOfWeekContaining(singaporeTodayYmd());
  utc.setUTCDate(utc.getUTCDate() + deltaWeeks * 7);
  return ymdFromUtcDate(utc);
}

export function formatWeekRangeLabel(mondayYmd: string): string {
  const start = mondayOfWeekContaining(mondayYmd);
  const end = sundayOfWeek(start);
  return `${formatSingaporeDate(start)} – ${formatSingaporeDate(end)}`;
}

/** Singapore calendar day of task due_date, else created_at. */
export function analyticsTaskDayKey(task: Pick<Task, 'due_date' | 'created_at'>): string | null {
  const raw = task.due_date || task.created_at;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(raw));
    return m?.[1] ?? null;
  }
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CRM_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

function weekdayShort(ymd: string): string {
  const utc = parseYmdUtc(ymd);
  if (!utc) return '';
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' }).format(utc);
}

/** Mon–Fri in UTC calendar day (Singapore YMD stored as civil date). */
function isWorkdayYmd(ymd: string): boolean {
  const utc = parseYmdUtc(ymd);
  if (!utc) return false;
  const dow = utc.getUTCDay();
  return dow >= 1 && dow <= 5;
}

function accountVisitKey(task: Pick<Task, 'client_id' | 'prospect_company_name'>): string | null {
  if (task.client_id != null && String(task.client_id).trim() !== '') {
    return `id:${String(task.client_id).trim()}`;
  }
  const prospect = String(task.prospect_company_name ?? '').trim().toLowerCase();
  return prospect ? `p:${prospect}` : null;
}

function projectStatusBucket(status?: string | null): 'ongoing' | 'onHold' | 'completed' {
  const s = String(status ?? 'Ongoing').trim().toLowerCase();
  if (s === 'completed') return 'completed';
  if (s === 'on hold' || s === 'onhold') return 'onHold';
  return 'ongoing';
}

function findClient(clients: Client[], id: string): Client | null {
  return clients.find((c) => idsMatch(c.id, id)) ?? null;
}

function extractThemes(descriptions: string[]): WeeklyThemeHit[] {
  const buckets = new Map<string, { count: number; examples: string[] }>();
  for (const text of descriptions) {
    const trimmed = text.trim();
    if (!trimmed) continue;
    for (const rule of THEME_RULES) {
      if (!rule.pattern.test(trimmed)) continue;
      const cur = buckets.get(rule.theme) ?? { count: 0, examples: [] };
      cur.count += 1;
      if (cur.examples.length < 2) {
        const snippet = trimmed.length > 120 ? `${trimmed.slice(0, 117)}…` : trimmed;
        cur.examples.push(snippet);
      }
      buckets.set(rule.theme, cur);
    }
  }
  return [...buckets.entries()]
    .map(([theme, v]) => ({ theme, count: v.count, examples: v.examples }))
    .sort((a, b) => b.count - a.count || a.theme.localeCompare(b.theme));
}

function buildNarrative(input: {
  weekLabel: string;
  visitCount: number;
  accountCount: number;
  notVisitedCount: number;
  themes: WeeklyThemeHit[];
  byOwner: WeeklyOwnerStat[];
  showOwnerCompare: boolean;
  projects: WeeklyProjectStat;
  newVisitsMonth: WeeklyNewVisitMonthStat;
  workdaysMet: number;
  workdayCount: number;
  highFrequency: WeeklyHighFreqAccount[];
  titleSlices: TaskTitlePieSlice[];
  projectUpdateHealth: WeeklyProjectUpdateHealth;
  dutyFollowUps: WeeklyDutyFollowUpStat;
  isTeamScope: boolean;
  staffDailyTargetMet: number;
  staffCount: number;
}): WeeklyNarrative {
  const {
    weekLabel,
    visitCount,
    accountCount,
    notVisitedCount,
    themes,
    byOwner,
    showOwnerCompare,
    projects,
    newVisitsMonth,
    workdaysMet,
    workdayCount,
    highFrequency,
    titleSlices,
    projectUpdateHealth,
    dutyFollowUps,
    isTeamScope,
    staffDailyTargetMet,
    staffCount,
  } = input;

  const mix = titleSlices
    .slice(0, 4)
    .map((s) => `${s.label} ${Math.round(s.pct)}%`)
    .join(', ');

  const newVisitLine = isTeamScope
    ? `New Visit target is ${newVisitsMonth.target} unique accounts per staff for ${newVisitsMonth.monthLabel}: ${newVisitsMonth.staffMet} of ${newVisitsMonth.staffCount} staff have already hit it (targets are not shared or pooled).`
    : `New Visit month to date (${newVisitsMonth.monthLabel}): ${newVisitsMonth.uniqueCount} of ${newVisitsMonth.target} unique accounts for this person${newVisitsMonth.met ? ' — target met' : ` — ${newVisitsMonth.remaining} still needed`}.`;

  const dailyLine = isTeamScope
    ? `Daily visit target is ${DAILY_VISIT_TARGET} visits per staff on each weekday: ${staffDailyTargetMet} of ${staffCount} staff met it every weekday this week (team totals do not count toward anyone’s personal target).`
    : `Weekday visit target (${DAILY_VISIT_TARGET} per day for this person): met on ${workdaysMet} of ${workdayCount} workday${workdayCount === 1 ? '' : 's'}.`;

  if (visitCount === 0) {
    return {
      summary: `For the week of ${weekLabel}, no visit tasks were recorded. Project pipeline: ${projects.total} total (${projects.ongoing} ongoing, ${projects.onHold} on hold, ${projects.completed} completed). Ongoing project updates: ${projectUpdateHealth.onTime} on time, ${projectUpdateHealth.stale} stale, ${projectUpdateHealth.neverUpdated} never updated. ${newVisitLine}`,
      recommendation: isTeamScope
        ? `Each salesperson still needs ${newVisitsMonth.target} unique New Visits this month and at least ${DAILY_VISIT_TARGET} visits every weekday — coach people who are behind, not the team total.`
        : !newVisitsMonth.met
          ? `Focus on New Visits — ${newVisitsMonth.remaining} more unique New Visit account${newVisitsMonth.remaining === 1 ? '' : 's'} needed this month (personal target ${newVisitsMonth.target}). Aim for at least ${DAILY_VISIT_TARGET} visits on each weekday.`
          : `Maintain at least ${DAILY_VISIT_TARGET} visits each weekday and keep clearing On Hold projects where possible.`,
      findings: [
        `Projects: ${projects.ongoing} ongoing, ${projects.onHold} on hold, ${projects.completed} completed.`,
        `Ongoing project updates: ${projectUpdateHealth.onTime} on time, ${projectUpdateHealth.stale} stale, ${projectUpdateHealth.neverUpdated} never updated.`,
        newVisitLine,
        notVisitedCount > 0
          ? `${notVisitedCount} accounts still have no visit tasks in the last 3 months.`
          : 'All accounts in scope have at least one visit in the last 3 months.',
      ],
    };
  }

  const summary = [
    `For the week of ${weekLabel}, ${visitCount} visit task${visitCount === 1 ? '' : 's'} covered ${accountCount} account${accountCount === 1 ? '' : 's'}${mix ? `, mainly ${mix}` : ''}.`,
    `Project pipeline has ${projects.total} project${projects.total === 1 ? '' : 's'}: ${projects.ongoing} ongoing, ${projects.onHold} on hold (need attention), and ${projects.completed} completed.`,
    projectUpdateHealth.ongoingTotal > 0
      ? `Of ${projectUpdateHealth.ongoingTotal} ongoing project${projectUpdateHealth.ongoingTotal === 1 ? '' : 's'}, ${projectUpdateHealth.onTime} have an on-time update (within ${PROJECT_UPDATE_ON_TIME_DAYS} days), ${projectUpdateHealth.stale} are stale, and ${projectUpdateHealth.neverUpdated} have never been updated.`
      : '',
    projects.problemProjects[0]
      ? `Projects under pressure include ${projects.problemProjects
          .slice(0, 3)
          .map((p) => `“${p.title}” (${p.companyName})`)
          .join('; ')}.`
      : 'No projects are currently On Hold.',
    newVisitLine,
    dailyLine,
    dutyFollowUps.withDuty > 0
      ? `Visit descriptions assigned ${dutyFollowUps.withDuty} dut${dutyFollowUps.withDuty === 1 ? 'y' : 'ies'}; ${dutyFollowUps.followedUp} have a later follow-up visit and ${dutyFollowUps.openCount} still look open.`
      : '',
    highFrequency[0]
      ? `High-frequency accounts this week (${HIGH_FREQ_VISIT_THRESHOLD} or more visits): ${highFrequency
          .slice(0, 3)
          .map((a) => `${a.companyName} (${a.visitCount})`)
          .join(', ')}.`
      : '',
    notVisitedCount > 0
      ? `${notVisitedCount} account${notVisitedCount === 1 ? '' : 's'} still show no visits in the last 3 months.`
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  const recommendations: string[] = [];
  if (isTeamScope) {
    const behindNew = byOwner.filter((o) => !o.newVisitMet);
    const behindDaily = byOwner.filter((o) => !o.dailyTargetMet);
    if (behindNew.length > 0) {
      recommendations.push(
        `Coach ${behindNew
          .slice(0, 5)
          .map((o) => o.ownerLabel)
          .join(', ')} on New Visits — each still needs their own ${NEW_VISIT_MONTHLY_TARGET} unique accounts this month.`,
      );
    }
    if (behindDaily.length > 0) {
      recommendations.push(
        `Lift weekday volume for ${behindDaily
          .slice(0, 5)
          .map((o) => o.ownerLabel)
          .join(', ')} to at least ${DAILY_VISIT_TARGET} visits each weekday (personal target, not a team pool).`,
      );
    }
  } else {
    if (!newVisitsMonth.met) {
      recommendations.push(
        `Book ${newVisitsMonth.remaining} more unique New Visit${newVisitsMonth.remaining === 1 ? '' : 's'} before month-end (your personal target is ${newVisitsMonth.target}).`,
      );
    }
    if (workdaysMet < workdayCount) {
      recommendations.push(
        `Lift your daily volume to at least ${DAILY_VISIT_TARGET} visits on weekdays that fell short.`,
      );
    }
  }
  if (projectUpdateHealth.stale + projectUpdateHealth.neverUpdated > 0) {
    recommendations.push(
      `Post updates on ${Math.min(projectUpdateHealth.stale + projectUpdateHealth.neverUpdated, 5)} ongoing project${projectUpdateHealth.stale + projectUpdateHealth.neverUpdated === 1 ? '' : 's'} that are past the ${PROJECT_UPDATE_ON_TIME_DAYS}-day update window.`,
    );
  }
  if (dutyFollowUps.openCount > 0) {
    recommendations.push(
      `Close out ${Math.min(dutyFollowUps.openCount, 5)} open dut${dutyFollowUps.openCount === 1 ? 'y' : 'ies'} from visit descriptions with a follow-up visit or clear completion note.`,
    );
  }
  if (projects.onHold > 0) {
    recommendations.push(
      `Unblock ${Math.min(projects.onHold, 3)} On Hold project${projects.onHold === 1 ? '' : 's'} — clarify next step or move status forward.`,
    );
  }
  if (notVisitedCount > 0) {
    recommendations.push(
      `Mix coverage visits for neglected accounts (${notVisitedCount} with no activity in 3 months).`,
    );
  }
  if (recommendations.length === 0) {
    recommendations.push(
      'Targets look healthy — deepen follow-ups on high-frequency accounts and keep New Visit quality high.',
    );
  }

  const findings: string[] = [
    `Projects: ${projects.ongoing} ongoing, ${projects.onHold} on hold, ${projects.completed} completed (of ${projects.total}).`,
    `Ongoing project updates: ${projectUpdateHealth.onTime} on time, ${projectUpdateHealth.stale} stale, ${projectUpdateHealth.neverUpdated} never updated.`,
    newVisitLine,
    dailyLine,
  ];
  if (dutyFollowUps.withDuty > 0) {
    findings.push(
      `Assigned duties in descriptions: ${dutyFollowUps.followedUp} followed up, ${dutyFollowUps.openCount} still open.`,
    );
  }
  if (themes.length > 0) {
    findings.push(
      `Description themes: ${themes
        .slice(0, 4)
        .map((t) => `${t.theme} (${t.count})`)
        .join('; ')}.`,
    );
  }
  if (showOwnerCompare && byOwner.length > 1) {
    const ranked = [...byOwner].sort((a, b) => b.visitCount - a.visitCount);
    findings.push(
      `By salesperson: ${ranked[0].ownerLabel} led with ${ranked[0].visitCount}; ${ranked[ranked.length - 1].ownerLabel} had ${ranked[ranked.length - 1].visitCount}.`,
    );
  }
  if (highFrequency.length > 0) {
    findings.push(
      `High-frequency visits: ${highFrequency
        .slice(0, 5)
        .map((a) => `${a.companyName} (${a.visitCount})`)
        .join(', ')}.`,
    );
  }

  return {
    summary,
    recommendation: recommendations.join(' '),
    findings,
  };
}

export type BuildCrmWeeklySummaryOptions = {
  weekMonday: string;
  tasks: Task[];
  projects?: Project[];
  updates?: ProjectUpdate[];
  clients: Client[];
  ownerLabel: (ownerRef?: string) => string;
  /** Not-visited rows from existing coverage (already scoped). */
  notVisited: AccountCoverageSummary[];
  /** Optional open/overdue task count outside the week filter (already scoped). */
  overdueOpenTasks?: number;
};

export function buildCrmWeeklySummary(options: BuildCrmWeeklySummaryOptions): CrmWeeklySummaryReport {
  const weekStart = mondayOfWeekContaining(options.weekMonday);
  const weekEnd = sundayOfWeek(weekStart);
  const weekLabel = formatWeekRangeLabel(weekStart);
  const monthKey = weekStart.slice(0, 7);
  const clientById = new Map(options.clients.map((c) => [String(c.id), c]));

  const weekTasks = options.tasks.filter((task) => {
    if (isCancelledTask(task)) return false;
    const day = analyticsTaskDayKey(task);
    if (!day) return false;
    return day >= weekStart && day <= weekEnd;
  });

  const visits: WeeklyVisitRow[] = weekTasks
    .map((task) => {
      const dayKey = analyticsTaskDayKey(task) ?? weekStart;
      const clientId =
        task.client_id != null && String(task.client_id).trim() !== ''
          ? String(task.client_id)
          : null;
      const client = clientId ? (findClient(options.clients, clientId) ?? clientById.get(clientId)) : null;
      const prospect = safeText(task.prospect_company_name, '');
      const companyName =
        client?.company_name?.trim() ||
        (prospect && prospect !== '—' ? prospect : 'No account linked');
      const description = String(task.description ?? '').trim();
      return {
        taskId: String(task.id),
        title: safeText(task.title, categorizeTaskTitle(String(task.title ?? ''))),
        status: safeText(task.status, '—'),
        dayKey,
        dayLabel: formatSingaporeDate(dayKey),
        ownerId: String(task.owner_id ?? ''),
        ownerLabel: options.ownerLabel(task.owner_id),
        clientId,
        companyName,
        code: safeText(client?.code, '—'),
        description,
        hasDescription: description.length > 0,
      };
    })
    .sort((a, b) => b.dayKey.localeCompare(a.dayKey) || a.companyName.localeCompare(b.companyName));

  const accountIds = new Set(
    visits.map((v) => v.clientId).filter((id): id is string => Boolean(id)),
  );
  const withDescription = visits.filter((v) => v.hasDescription).length;
  const withoutDescription = visits.length - withDescription;
  const descriptionRate = visits.length === 0 ? 0 : withDescription / visits.length;

  const weekDayKeys: string[] = [];
  for (let i = 0; i < 7; i += 1) {
    const utc = parseYmdUtc(weekStart)!;
    utc.setUTCDate(utc.getUTCDate() + i);
    weekDayKeys.push(ymdFromUtcDate(utc));
  }
  const workdayKeys = weekDayKeys.filter((d) => isWorkdayYmd(d));

  /** Per-person New Visit unique keys this month (ownerKey → account keys). */
  const newVisitByOwner = new Map<string, Set<string>>();
  for (const task of options.tasks) {
    if (isCancelledTask(task)) continue;
    if (categorizeTaskTitle(String(task.title ?? '')) !== 'New Visit') continue;
    const day = analyticsTaskDayKey(task);
    if (!day || !day.startsWith(monthKey)) continue;
    const accountKey = accountVisitKey(task);
    if (!accountKey) continue;
    const ownerKey =
      String(task.owner_id ?? '').trim() || options.ownerLabel(task.owner_id) || 'unknown';
    const set = newVisitByOwner.get(ownerKey) ?? new Set<string>();
    set.add(accountKey);
    newVisitByOwner.set(ownerKey, set);
  }

  /** Per-person visit counts by day this week. */
  const ownerDayCounts = new Map<string, Map<string, number>>();
  for (const v of visits) {
    const ownerKey = v.ownerId || v.ownerLabel || 'unknown';
    const dayMap = ownerDayCounts.get(ownerKey) ?? new Map<string, number>();
    dayMap.set(v.dayKey, (dayMap.get(v.dayKey) ?? 0) + 1);
    ownerDayCounts.set(ownerKey, dayMap);
  }

  const ownerMap = new Map<string, WeeklyOwnerStat>();
  for (const v of visits) {
    const key = v.ownerId || v.ownerLabel;
    const cur =
      ownerMap.get(key) ??
      ({
        ownerId: v.ownerId,
        ownerLabel: v.ownerLabel,
        visitCount: 0,
        accountCount: 0,
        withDescription: 0,
        descriptionRate: 0,
        newVisitUnique: 0,
        newVisitTarget: NEW_VISIT_MONTHLY_TARGET,
        newVisitMet: false,
        newVisitRemaining: NEW_VISIT_MONTHLY_TARGET,
        workdaysMet: 0,
        workdayCount: workdayKeys.length,
        dailyTargetMet: false,
      } satisfies WeeklyOwnerStat);
    cur.visitCount += 1;
    if (v.hasDescription) cur.withDescription += 1;
    ownerMap.set(key, cur);
  }
  // Include owners who have New Visits this month but zero visits this week.
  for (const [ownerKey, keys] of newVisitByOwner) {
    if (ownerMap.has(ownerKey)) continue;
    ownerMap.set(ownerKey, {
      ownerId: ownerKey,
      ownerLabel: options.ownerLabel(ownerKey) || ownerKey,
      visitCount: 0,
      accountCount: 0,
      withDescription: 0,
      descriptionRate: 0,
      newVisitUnique: keys.size,
      newVisitTarget: NEW_VISIT_MONTHLY_TARGET,
      newVisitMet: keys.size >= NEW_VISIT_MONTHLY_TARGET,
      newVisitRemaining: Math.max(0, NEW_VISIT_MONTHLY_TARGET - keys.size),
      workdaysMet: 0,
      workdayCount: workdayKeys.length,
      dailyTargetMet: false,
    });
  }
  for (const [key, cur] of ownerMap) {
    const accounts = new Set(
      visits.filter((v) => (v.ownerId || v.ownerLabel) === key && v.clientId).map((v) => v.clientId!),
    );
    cur.accountCount = accounts.size;
    cur.descriptionRate = cur.visitCount === 0 ? 0 : cur.withDescription / cur.visitCount;
    const nv = newVisitByOwner.get(key)?.size ?? 0;
    cur.newVisitUnique = nv;
    cur.newVisitMet = nv >= NEW_VISIT_MONTHLY_TARGET;
    cur.newVisitRemaining = Math.max(0, NEW_VISIT_MONTHLY_TARGET - nv);
    const dayMap = ownerDayCounts.get(key) ?? new Map<string, number>();
    cur.workdaysMet = workdayKeys.filter((d) => (dayMap.get(d) ?? 0) >= DAILY_VISIT_TARGET).length;
    cur.workdayCount = workdayKeys.length;
    cur.dailyTargetMet = cur.workdayCount > 0 && cur.workdaysMet === cur.workdayCount;
  }
  const byOwner = [...ownerMap.values()].sort(
    (a, b) => b.visitCount - a.visitCount || a.ownerLabel.localeCompare(b.ownerLabel),
  );

  const isTeamScope = byOwner.length > 1;
  const staffCount = byOwner.length;
  const staffMetNewVisit = byOwner.filter((o) => o.newVisitMet).length;
  const staffDailyTargetMet = byOwner.filter((o) => o.dailyTargetMet).length;

  const byDayMap = new Map<string, number>();
  for (const dayKey of weekDayKeys) byDayMap.set(dayKey, 0);
  for (const v of visits) {
    byDayMap.set(v.dayKey, (byDayMap.get(v.dayKey) ?? 0) + 1);
  }
  const byDay: WeeklyDayStat[] = [...byDayMap.entries()].map(([dayKey, visitCount]) => {
    const workday = isWorkdayYmd(dayKey);
    // Team bars are volume only — personal daily target is never scored on the summed total.
    const metDailyTarget = isTeamScope
      ? false
      : workday
        ? visitCount >= DAILY_VISIT_TARGET
        : visitCount > 0;
    return {
      dayKey,
      dayLabel: formatSingaporeDate(dayKey),
      weekdayShort: weekdayShort(dayKey),
      visitCount,
      isWorkday: workday,
      metDailyTarget,
    };
  });

  const primary = byOwner.length === 1 ? byOwner[0] : null;
  const workdayCount = primary?.workdayCount ?? workdayKeys.length;
  const workdaysMet = primary?.workdaysMet ?? 0;

  const newVisitsMonth: WeeklyNewVisitMonthStat = isTeamScope
    ? {
        monthKey,
        monthLabel: formatTaskMonthLabel(monthKey),
        uniqueCount: staffMetNewVisit,
        target: NEW_VISIT_MONTHLY_TARGET,
        met: staffCount > 0 && staffMetNewVisit === staffCount,
        remaining: Math.max(0, staffCount - staffMetNewVisit),
        isTeamRate: true,
        staffMet: staffMetNewVisit,
        staffCount,
      }
    : {
        monthKey,
        monthLabel: formatTaskMonthLabel(monthKey),
        uniqueCount: primary?.newVisitUnique ?? 0,
        target: NEW_VISIT_MONTHLY_TARGET,
        met: primary?.newVisitMet ?? false,
        remaining: primary?.newVisitRemaining ?? NEW_VISIT_MONTHLY_TARGET,
        isTeamRate: false,
        staffMet: primary?.newVisitMet ? 1 : 0,
        staffCount: primary ? 1 : 0,
      };

  const freqMap = new Map<string, WeeklyHighFreqAccount>();
  for (const v of visits) {
    if (!v.clientId) continue;
    const cur =
      freqMap.get(v.clientId) ??
      ({
        clientId: v.clientId,
        companyName: v.companyName,
        code: v.code,
        visitCount: 0,
      } satisfies WeeklyHighFreqAccount);
    cur.visitCount += 1;
    freqMap.set(v.clientId, cur);
  }
  const highFrequency = [...freqMap.values()]
    .filter((a) => a.visitCount >= HIGH_FREQ_VISIT_THRESHOLD)
    .sort((a, b) => b.visitCount - a.visitCount || a.companyName.localeCompare(b.companyName));

  const projectList = options.projects ?? [];
  let ongoing = 0;
  let onHold = 0;
  let completed = 0;
  const problemProjects: WeeklyProjectStat['problemProjects'] = [];
  for (const project of projectList) {
    const bucket = projectStatusBucket(project.status);
    if (bucket === 'completed') completed += 1;
    else if (bucket === 'onHold') {
      onHold += 1;
      const client = project.client_id
        ? (findClient(options.clients, String(project.client_id)) ??
          clientById.get(String(project.client_id)))
        : null;
      problemProjects.push({
        id: String(project.id),
        title: safeText(project.title, 'Untitled project'),
        companyName: client?.company_name?.trim() || 'No account linked',
        status: safeText(project.status, 'On Hold'),
      });
    } else ongoing += 1;
  }
  problemProjects.sort((a, b) => a.companyName.localeCompare(b.companyName));
  const projects: WeeklyProjectStat = {
    total: projectList.length,
    ongoing,
    onHold,
    completed,
    problemProjects: problemProjects.slice(0, 12),
  };

  const updates = options.updates ?? [];
  const latestHumanUpdateByProject = new Map<string, string>();
  for (const update of updates) {
    if (isSystemProjectUpdate(update.content)) continue;
    const at = String(update.created_at ?? '').trim();
    if (!at) continue;
    const pid = String(update.project_id ?? '');
    if (!pid) continue;
    const prev = latestHumanUpdateByProject.get(pid);
    if (!prev || at > prev) latestHumanUpdateByProject.set(pid, at);
  }
  const todayYmd = singaporeTodayYmd();
  const todayUtc = parseYmdUtc(todayYmd) ?? new Date();
  let onTime = 0;
  let stale = 0;
  let neverUpdated = 0;
  const staleProjects: WeeklyStaleProject[] = [];
  for (const project of projectList) {
    if (projectStatusBucket(project.status) !== 'ongoing') continue;
    const lastAt = latestHumanUpdateByProject.get(String(project.id)) ?? null;
    const client = project.client_id
      ? (findClient(options.clients, String(project.client_id)) ??
        clientById.get(String(project.client_id)))
      : null;
    if (!lastAt) {
      neverUpdated += 1;
      staleProjects.push({
        id: String(project.id),
        title: safeText(project.title, 'Untitled project'),
        companyName: client?.company_name?.trim() || 'No account linked',
        lastUpdateAt: null,
        daysSinceUpdate: null,
      });
      continue;
    }
    const lastDay = lastAt.slice(0, 10);
    const lastUtc = parseYmdUtc(lastDay);
    const daysSince = lastUtc
      ? Math.max(0, Math.round((todayUtc.getTime() - lastUtc.getTime()) / 86_400_000))
      : null;
    if (daysSince != null && daysSince <= PROJECT_UPDATE_ON_TIME_DAYS) {
      onTime += 1;
    } else {
      stale += 1;
      staleProjects.push({
        id: String(project.id),
        title: safeText(project.title, 'Untitled project'),
        companyName: client?.company_name?.trim() || 'No account linked',
        lastUpdateAt: lastAt,
        daysSinceUpdate: daysSince,
      });
    }
  }
  staleProjects.sort((a, b) => {
    const da = a.daysSinceUpdate ?? 9999;
    const db = b.daysSinceUpdate ?? 9999;
    return db - da || a.companyName.localeCompare(b.companyName);
  });
  const projectUpdateHealth: WeeklyProjectUpdateHealth = {
    ongoingTotal: ongoing,
    onTime,
    stale,
    neverUpdated,
    staleProjects: staleProjects.slice(0, 12),
  };

  /** Later tasks by account for follow-up detection (all non-cancelled). */
  const tasksByAccountDay = new Map<string, string[]>();
  for (const task of options.tasks) {
    if (isCancelledTask(task)) continue;
    const day = analyticsTaskDayKey(task);
    const key = accountVisitKey(task);
    if (!day || !key) continue;
    const list = tasksByAccountDay.get(key) ?? [];
    list.push(day);
    tasksByAccountDay.set(key, list);
  }
  for (const [key, days] of tasksByAccountDay) {
    days.sort();
    tasksByAccountDay.set(key, days);
  }

  const dutyItems: WeeklyDutyFollowUp[] = [];
  for (const visit of visits) {
    if (!visit.hasDescription || !DUTY_PATTERN.test(visit.description)) continue;
    const accountKey = visit.clientId
      ? `id:${visit.clientId}`
      : accountVisitKey({
          client_id: visit.clientId,
          prospect_company_name: visit.companyName === 'No account linked' ? null : visit.companyName,
        });
    let followedUp = FOLLOW_UP_DONE_PATTERN.test(visit.description);
    if (!followedUp && accountKey) {
      const days = tasksByAccountDay.get(accountKey) ?? [];
      followedUp = days.some((d) => d > visit.dayKey);
    }
    const snippet =
      visit.description.length > 140 ? `${visit.description.slice(0, 137)}…` : visit.description;
    dutyItems.push({
      taskId: visit.taskId,
      companyName: visit.companyName,
      dayLabel: visit.dayLabel,
      ownerLabel: visit.ownerLabel,
      snippet,
      followedUp,
    });
  }
  const dutyFollowUps: WeeklyDutyFollowUpStat = {
    withDuty: dutyItems.length,
    followedUp: dutyItems.filter((d) => d.followedUp).length,
    openCount: dutyItems.filter((d) => !d.followedUp).length,
    items: dutyItems.slice(0, 20),
  };

  const themes = extractThemes(visits.filter((v) => v.hasDescription).map((v) => v.description));
  const notVisited = [...options.notVisited].sort((a, b) =>
    a.companyName.localeCompare(b.companyName, undefined, { sensitivity: 'base' }),
  );
  const titleSlices = buildTaskTitlePieSlicesFromTasks(weekTasks);

  const narrative = buildNarrative({
    weekLabel,
    visitCount: visits.length,
    accountCount: accountIds.size,
    notVisitedCount: notVisited.length,
    themes,
    byOwner,
    showOwnerCompare: byOwner.length > 1,
    projects,
    newVisitsMonth,
    workdaysMet,
    workdayCount,
    highFrequency,
    titleSlices,
    projectUpdateHealth,
    dutyFollowUps,
    isTeamScope,
    staffDailyTargetMet,
    staffCount,
  });

  return {
    weekStart,
    weekEnd,
    weekLabel,
    visitCount: visits.length,
    accountCount: accountIds.size,
    withDescription,
    withoutDescription,
    descriptionRate,
    overdueOpenTasks: options.overdueOpenTasks ?? 0,
    projects,
    newVisitsMonth,
    dailyTarget: DAILY_VISIT_TARGET,
    workdaysMet,
    workdayCount,
    isTeamScope,
    staffDailyTargetMet,
    staffCount,
    highFrequency,
    projectUpdateHealth,
    dutyFollowUps,
    visits,
    byDay,
    byOwner,
    titleSlices,
    themes,
    notVisited,
    notVisitedCount: notVisited.length,
    narrative,
  };
}
